import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { getCommitteeDataTableStructure } from '@/pages/settings/committee/committee-history/utils/committee-history-data-table-object';
import { CommitteeHistory } from '@/types';
import { CommitteeService } from '@/pages/settings/committee/committee.service';
import { BehaviorSubject, combineLatest, map, startWith } from 'rxjs';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { ButtonModule } from 'primeng/button';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { getCommitteeHistoryFiltersFormObject } from '@/pages/settings/committee/committee-history/utils/committee-history-filters-form-object';
import { format } from 'date-fns';

@Component({
  selector: 'sm-committee-history',
  imports: [PageHeaderComponent, DataTableBuilderComponent, CommonModule, TranslateModule, FormBuilderComponent, ButtonModule],
  templateUrl: './committee-history.component.html',
  styleUrl: './committee-history.component.scss',
})
export class CommitteeHistoryComponent implements OnInit {
  translate = inject(TranslateService);
  messageService = inject(MessageService);
  committeeService = inject(CommitteeService);

  breadcrumb = [
    {
      label: 'settings.committee.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.committee.breadcrumb.committee',
      routerLink: ['/settings', 'committee'],
    },
    {
      label: 'settings.committee.breadcrumb.history',
      routerLink: ['/settings', 'committee', 'history'],
    },
  ];
  serverQuery = createServerQuery({});
  dataTableObject: DynamicDataTable = getCommitteeDataTableStructure({
    serverQuery: this.serverQuery,
  });
  loading = false;
  data: CommitteeHistory[] = [];
  private selectedCommittee$ = new BehaviorSubject<SelectOption | undefined>(undefined);
  selectedDate$ = new BehaviorSubject<Date | null>(null);
  totalRecords = 0;
  committeesOptions: SelectOption[] = [];
  formObject: DynamicForm = getCommitteeHistoryFiltersFormObject({
    committeeOptions: this.committeesOptions,
    selectedCommittee$: this.selectedCommittee$,
    selectedDate$: this.selectedDate$,
  });
  private activeCommitteeId = signal<number | null>(null);
  private activeDate = signal<string | null>(null);
  // no URL parameter usage; selection comes from the select field

  isSearchDisabled$ = combineLatest([this.selectedCommittee$, this.selectedDate$]).pipe(
    map(([committee, selectedDate]) => !committee || !selectedDate),
    startWith(true)
  );

  constructor() {
    effect(() => {
      const committeeId = this.activeCommitteeId();
      const exactDate = this.activeDate();

      if (!committeeId || !exactDate) {
        return;
      }

      const page = this.serverQuery.page();
      const size = this.serverQuery.pageSize();
      const sortBy = this.serverQuery.sortBy();
      const sortOrder = this.serverQuery.sortOrder();
      const search = this.serverQuery.search();
      const sort = sortBy ? `${sortBy},${sortOrder}` : undefined;

      this.loadData(committeeId, exactDate, page, size, sort, search);
    });
  }

  ngOnInit(): void {
    this.committeeService.getCommitteesList().subscribe({
      next: response => {
        this.committeesOptions = response.map(committee => ({ name: committee.nom, code: committee.id }));
        this.formObject = getCommitteeHistoryFiltersFormObject({
          committeeOptions: this.committeesOptions,
          selectedCommittee$: this.selectedCommittee$,
          selectedDate$: this.selectedDate$,
        });
      },
    });
  }

  confirm(): void {
    const committee = this.selectedCommittee$.getValue();
    const selectedDate = this.selectedDate$.getValue();

    if (!committee || !selectedDate) {
      return;
    }

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    this.serverQuery.setPage(0);
    this.activeCommitteeId.set(Number(committee.code));
    this.activeDate.set(formattedDate);
  }

  private loadData(committeeId: number, formattedDate: string, page = 0, size = 10, sort?: string, search?: string): void {
    this.loading = true;
    this.committeeService.getCommitteeHistory(committeeId, { page, size, sort, search }, formattedDate).subscribe({
      next: response => {
        this.data = response.content;
        this.totalRecords = response.totalElements;
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('settings.committee.message.error.loadingHistory'),
        });
      },
    });
  }
}
